import { useEffect, useRef, useState } from 'react';
import { supabase } from '../config/supabase';

interface WebRTCConfig {
  userId: string;
  targetUserId?: string; // Opcional: solo necesario para supervisores
  isInitiator?: boolean;
  isSupervisor: boolean;
}

export const useWebRTC = ({ userId, targetUserId, isInitiator = false, isSupervisor }: WebRTCConfig) => {
  const peerConnection = useRef<RTCPeerConnection | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isConnected, setIsConnected] = useState(false);
  const localStream = useRef<MediaStream | null>(null);
  const remoteStream = useRef<MediaStream | null>(null);

  // Configurar la conexión WebRTC
  useEffect(() => {
    const setupPeerConnection = async () => {
      try {
        // Configurar la conexión peer
        peerConnection.current = new RTCPeerConnection({
          iceServers: [
            { urls: 'stun:stun.l.google.com:19302' }
          ]
        });

        // Manejar cambios de estado de conexión
        peerConnection.current.onconnectionstatechange = () => {
          if (peerConnection.current?.connectionState === 'connected') {
            setIsConnected(true);
          } else {
            setIsConnected(false);
          }
        };

        // Manejar streams remotos
        peerConnection.current.ontrack = (event) => {
          remoteStream.current = event.streams[0];
        };

        // Si es empleado, configurar la cámara
        if (!isSupervisor) {
          const stream = await navigator.mediaDevices.getUserMedia({
            video: {
              width: { ideal: 1280 },
              height: { ideal: 720 },
              frameRate: { max: 30 }
            }
          });

          localStream.current = stream;
          stream.getTracks().forEach(track => {
            if (peerConnection.current && localStream.current) {
              peerConnection.current.addTrack(track, localStream.current);
            }
          });
        }

        // Si es supervisor y es iniciador, crear y enviar la oferta
        if (isSupervisor && isInitiator && targetUserId) {
          const offer = await peerConnection.current.createOffer();
          await peerConnection.current.setLocalDescription(offer);

          const { error: insertError } = await supabase
            .from('webrtc_signaling')
            .insert({
              from_user_id: userId,
              to_user_id: targetUserId,
              type: 'offer',
              payload: offer
            });

          if (insertError) {
            throw new Error(`Error al enviar la oferta: ${insertError.message}`);
          }
        }

      } catch (err) {
        setError('Error al configurar la conexión WebRTC');
        console.error(err);
      }
    };

    // Iniciar solo si:
    // - Es supervisor y tiene targetUserId
    // - O es empleado (sin importar targetUserId)
    if ((isSupervisor && targetUserId) || !isSupervisor) {
      setupPeerConnection();
    }

    return () => {
      // Limpiar la conexión
      if (peerConnection.current) {
        peerConnection.current.close();
      }
      // Detener streams
      if (localStream.current) {
        localStream.current.getTracks().forEach(track => track.stop());
      }
    };
  }, [userId, targetUserId, isInitiator, isSupervisor]);

  // Escuchar señalización WebRTC
  useEffect(() => {
    const subscription = supabase
      .channel('webrtc')
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'webrtc_signaling',
          filter: `to_user_id=eq.${userId}`
        },
        async (payload) => {
          if (!peerConnection.current) return;

          const { id, from_user_id, type, payload: signalingPayload } = payload.new;

          try {
            // Procesar el mensaje según su tipo
            if (type === 'offer') {
              // Solo el empleado debe responder a las ofertas
              if (!isSupervisor) {
                await peerConnection.current.setRemoteDescription(signalingPayload);
                const answer = await peerConnection.current.createAnswer();
                await peerConnection.current.setLocalDescription(answer);

                const { error: insertError } = await supabase
                  .from('webrtc_signaling')
                  .insert({
                    from_user_id: userId,
                    to_user_id: from_user_id,
                    type: 'answer',
                    payload: answer
                  });

                if (insertError) {
                  throw new Error(`Error al enviar la respuesta: ${insertError.message}`);
                }
              }
            } else if (type === 'answer') {
              // Solo el supervisor debe procesar las respuestas
              if (isSupervisor) {
                await peerConnection.current.setRemoteDescription(signalingPayload);
              }
            } else if (type === 'ice-candidate') {
              await peerConnection.current.addIceCandidate(signalingPayload);
            }

            // Marcar el mensaje como leído
            const { error: updateError } = await supabase
              .from('webrtc_signaling')
              .update({ read_at: new Date().toISOString() })
              .eq('id', id);

            if (updateError) {
              console.error('Error al marcar mensaje como leído:', updateError);
            }

          } catch (err) {
            setError('Error en la señalización WebRTC');
            console.error(err);
          }
        }
      )
      .subscribe();

    return () => {
      subscription.unsubscribe();
    };
  }, [userId, isSupervisor]);

  // Manejar candidatos ICE
  useEffect(() => {
    if (!peerConnection.current) return;

    peerConnection.current.onicecandidate = async (event) => {
      if (event.candidate && (isSupervisor ? targetUserId : true)) {
        const { error: insertError } = await supabase
          .from('webrtc_signaling')
          .insert({
            id: crypto.randomUUID(),  
            from_user_id: userId,
            to_user_id: isSupervisor ? targetUserId : null,  
            type: 'ice-candidate',
            payload: event.candidate,
            created_at: new Date().toISOString()  
          });

        if (insertError) {
          console.error('Error al enviar candidato ICE:', insertError);
        }
      }
    };
  }, [userId, targetUserId, isSupervisor]);

  return {
    peerConnection: peerConnection.current,
    localStream: localStream.current,
    remoteStream: remoteStream.current,
    error,
    isConnected
  };
};

export default useWebRTC;
